import { Umbrella, Sun } from "lucide-react";
import { fmtTime } from "./palette";

const dayWords = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });

const chipLabel = (iso, index) => {
  if (index === 0) return "Today";
  if (index === 1) return "Tomorrow";
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
};

// What each bar colour means.
const TONES = {
  dry: { colour: "#94A3B8", label: "Dry — under 30% chance" },
  showers: { colour: "#38BDF8", label: "Showers possible — 30% to 59%" },
  rain: { colour: "#2563EB", label: "Rain likely — 60% or more" },
  snow: { colour: "#E0F2FE", label: "Snow" },
  storm: { colour: "#A855F7", label: "Thunderstorms" },
};

const toneOf = (hour) => {
  const { code, precip } = hour;
  if (code >= 95) return "storm";
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "snow";
  if (precip >= 60) return "rain";
  if (precip >= 30) return "showers";
  return "dry";
};

export const HourlyRain = ({ data, accent, clock, day, days, onPickDay }) => {
  const hours = (data?.hours || []).filter((h) => Number(h.hour.slice(0, 2)) >= 5);
  const best = data?.windows?.[0];
  const present = [...new Set(hours.map(toneOf))];
  const legend = ["dry", "showers", "rain", "snow", "storm"]
    .filter((key) => present.includes(key) || ["dry", "showers", "rain"].includes(key));

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="hourly-rain">
      <div className="flex flex-wrap items-center gap-3">
        <p className="inline-flex items-center gap-2 text-[13px] uppercase tracking-widest" style={{ color: accent }}>
          <Umbrella className="h-4 w-4" /> Hour by hour rain
        </p>
        {data?.place?.name && <span className="text-sm text-ink-soft">{data.place.name}</span>}
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="hourly-days">
        {days.map((value, index) => (
          <button
            key={value}
            data-testid={`hourly-pick-${index === 0 ? "today" : index === 1 ? "tomorrow" : value}`}
            onClick={() => onPickDay(value)}
            aria-pressed={day === value}
            className="rounded-full border px-4 py-1.5 text-sm font-semibold"
            style={day === value
              ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
              : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
          >
            {chipLabel(value, index)}
          </button>
        ))}
      </div>

      {!hours.length && (
        <p className="mt-2 text-xs text-ink-soft">
          {data?.place
            ? "No hourly forecast for that day yet."
            : "Set your place in the weather panel above and I'll break the day down hour by hour."}
        </p>
      )}

      {hours.length > 0 && (
        <>
          <p className="mt-2 text-sm text-ink-soft" data-testid="hourly-best-window">
            {best ? (
              <>
                <Sun className="me-1 inline h-4 w-4" style={{ color: accent }} />
                Driest stretch on {dayWords(data.day)}:{" "}
                <span className="font-semibold text-ink">
                  {fmtTime(best.from, clock)} to {fmtTime(best.to, clock)}
                </span>{" "}
                — {best.hours} hours under a 30% chance.
              </>
            ) : (
              <>No properly dry window on {dayWords(data.day)} — plan for rain.</>
            )}
          </p>

          <div className="mt-3 flex items-end gap-[4px] overflow-x-auto pb-1" data-testid="hourly-bars">
            {hours.map((h) => {
              const tone = TONES[toneOf(h)];
              const inBest = best && h.hour >= best.from && h.hour <= best.to;
              return (
                <span
                  key={h.hour}
                  title={`${h.hour} · ${tone.label} (${h.precip}%)${h.mm ? `, ${h.mm}mm` : ""} · ${h.temp}°`}
                  data-testid={`hourly-hour-${h.hour}`}
                  className="flex w-[32px] shrink-0 flex-col items-center gap-1 rounded"
                  style={{ backgroundColor: inBest ? `${accent}1A` : "transparent" }}
                >
                  <span className="text-[11px] tabular-nums text-ink-soft">{h.precip}</span>
                  <span className="flex h-20 w-full items-end rounded bg-white/5">
                    <span
                      className="w-full rounded transition-all"
                      style={{ height: `${Math.max(4, h.precip)}%`, backgroundColor: tone.colour }}
                    />
                  </span>
                  <span className="text-[11px] tabular-nums text-ink-soft">{h.hour.slice(0, 2)}</span>
                  <span className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: inBest ? accent : "transparent" }} />
                </span>
              );
            })}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="hourly-legend">
            {legend.map((key) => (
              <span key={key} className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: TONES[key].colour }} />
                {TONES[key].label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
              <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: `${accent}55` }} />
              Driest run of the day
            </span>
          </div>
          <p className="mt-1 text-xs text-ink-soft">
            The number above each bar is the chance of rain in that hour.
          </p>
        </>
      )}
    </div>
  );
};
